<template>
  <div class="d-flex flex-column">
    <div class="grid-header">
      <h1>{{ $t("inmuebles.titulo") }}</h1>
      <div class="toolbar">
        <button type="button" @click.prevent="crearInmueble()" class="btn btn-link">
          <i class="fa-solid fa-plus"></i>
        </button>
      </div>
    </div>

    <DxDataGrid v-bind="getDefaultGridProperties()" :data-source="gridSource" ref="grid"
      class="dx-card wide-card grid-inmuebles">
      <DxColumnFixing :enabled="true" />

      <DxColumn data-field="Tipo" :caption="$t('inmuebles.grid.tipo')" cell-template="tipoTemplate">
        <DxLookup v-bind="{ ...getLookup('inmuebles', 'tipo') }"></DxLookup>
      </DxColumn>
      <DxColumn data-field="Direccion" :caption="$t('inmuebles.grid.direccion')"></DxColumn>
      <DxColumn data-field="Municipio" :caption="$t('inmuebles.grid.localidad')"></DxColumn>
      <DxColumn data-field="Delegacion" :caption="$t('inmuebles.grid.delegacion')"></DxColumn>
      <DxColumn data-field="Servicios" :caption="$t('inmuebles.grid.servicios')" cell-template="serviciosTemplate"
        :calculate-cell-value="calculateServicios"
        :calculate-filter-expression="calculateFilterExpression">
        <DxLookup v-bind="{ 
          dataSource: serviciosDisponibles,
          displayExpr: 'text',
          valueExpr: 'value',
          searchMode: 'contains'
        }" />
      </DxColumn>
      <DxColumn type="buttons" :caption="$t('inmuebles.grid.acciones')" :fixed="true"
        fixed-position="right" :buttons="[
          {
            cssClass: 'btn btn-link text-decoration-none btn-sm',
            icon: 'fa-regular fa-edit text-success',
            hint: $t('inmuebles.grid.modificar'),
            onClick: (e: any) => verDetalle(e.row.data.Id)
          },
          {
            cssClass: 'btn btn-link text-decoration-none btn-sm',
            icon: 'fa-regular fa-trash text-success',
            hint: $t('inmuebles.grid.borrar'),
            onClick: (e: any) => borrar(e.row.data.Id)
          },
          {
            cssClass: 'btn btn-link text-decoration-none btn-sm',
            icon: 'fa-regular fa-plus text-success',
            hint: $t('inmuebles.grid.nueva_venta'),
            onClick: (e: any) => nuevaVenta(e.row.data.Id)
          }
        ]"/>

      <template #serviciosTemplate="{ data }">
          <div v-for="servicio in data.value" :key="servicio" :class="[
            `d-inline-block rounded py-1 px-2 me-2`,
            `servicio ${servicio.replace(' ', '')}`
          ]">
            <i :class="[
              'me-1',
              servicio === 'AGUA' ? 'fa-regular fa-droplet' :
                servicio === 'LUZ' ? 'fa-regular fa-lightbulb' :
                  'fa-regular fa-fire'
            ]"></i>
            {{ $t(`inmuebles.servicios.${servicio}`) }}
          </div>
      </template>

      <template #tipoTemplate="{ data }">
        <div :class="[
          `d-inline-block rounded py-1 px-2`,
          `tipologia ${data.value.replace(' ', '')}`,
        ]">
          {{ $t(`inmuebles.tipo.${data.value}`) }}
        </div>
      </template>
    </DxDataGrid>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref } from "vue";
import { DxDataGrid, DxColumn, DxColumnFixing, DxLookup, DxFilterBuilder } from "devextreme-vue/data-grid";
import { getDefaultGridProperties, getLookup } from "@helpers/grid-helper";
import { useApi } from "@api/integration";
import type CustomStore from "devextreme/data/custom_store";
import { useIntl } from "@services/i18n";
import { useModalService } from "@services/modal-service";
import { type DXLoadOptions } from "@/.shared/.api";
import { messageBoxResult } from "@/.shared/components/dialogs/message-box";
import { toastError, toastSuccess } from "@/.shared/services/notification-service";
import { CreateInmueble } from "./models/CreateInmueble";
import InmuebleForm from "./InmuebleForm.vue";
import { useRouter } from "@/.shared/services/routing-services";

const intl = useIntl();
const api = useApi();
const grid = ref<DxDataGrid>();
const gridSource = ref<CustomStore>();
const modal = useModalService();
const router = useRouter();

const serviciosDisponibles = [
  { text: intl.i18n.t('inmuebles.servicios.AGUA'), value: 'AGUA' },
  { text: intl.i18n.t('inmuebles.servicios.LUZ'), value: 'LUZ' },
  { text: intl.i18n.t('inmuebles.servicios.GAS'), value: 'GAS' }
];

onMounted(async () => {
  await setDataSource();
});

async function crearInmueble() {
  let title = `${intl.i18n.t("inmuebles.creacion.titulo")}`;
  modal.showModal(
    InmuebleForm,
    { model: new CreateInmueble(), isEditMode: false },
    { title: title, width: 1200, height: 700 }
  ).then((value) => {
    if (value == messageBoxResult.ok)
      grid.value?.instance?.refresh();
  });
}

const setDataSource = async () => {

  gridSource.value = api.apiStore({
    key: "Id",
    load: async (p) => {
      const q = p as DXLoadOptions;
      return await api.callStandardApi("getInmueblesForGrid", { showLoading: false }, q)
    }
  });
}

const verDetalle = async (id: string) => {
  let title = `${intl.i18n.t("inmuebles.edicion.titulo")}`;
  await modal.showModal(
    InmuebleForm,
    { model: await api.callStandardApi("getInmuebleById", { showLoading: true }, id), isEditMode: true },
    { title: title, width: 1200, height: 700 }
  ).then((value) => {
    if (value == messageBoxResult.ok)
      grid.value?.instance?.refresh();
  });
}

const borrar = async (id: string) => {
  let respuesta = await modal.messageBox(intl.i18n.t('inmuebles.grid.borrar_pregunta'), intl.i18n.t('inmuebles.grid.titulo_confirma_borrar'), "okCancel", "warning");
  if (respuesta == messageBoxResult.ok)
    await api.callStandardApi("deleteInmueble", { showLoading: true }, id)
      .then(() => {
        toastSuccess(intl.i18n.t('inmuebles.borrado_exito'));
        grid.value?.instance?.refresh();
      })
      .catch(() => toastError(intl.i18n.t('agentes.borrado_fallo')));
}

const calculateServicios = (rowData: any) => {
  const servicios: string[] = [];
  if (rowData.TieneServicioAgua) servicios.push('AGUA');
  if (rowData.TieneServicioLuz) servicios.push('LUZ');
  if (rowData.TieneServicioGas) servicios.push('GAS');
  return servicios;
};

const calculateFilterExpression = (filterValue: any, selectedFilterOperation: string) => {
  if (!filterValue) return null;
  
  switch(filterValue) {
    case 'AGUA': return ['TieneServicioAgua', '=', true];
    case 'LUZ': return ['TieneServicioLuz', '=', true];
    case 'GAS': return ['TieneServicioGas', '=', true];
    default: return null;
  }
};

const nuevaVenta = async (id: string) => {
  await router.push(intl.i18Route({ name: "nueva-venta-inmobiliaria", query: { idInmueble: id } }));
}

</script>
